"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { MilestoneFlagIcon } from "./MilestoneFlagIcon";

export interface LifeMilestone1MetadataRow {
  id?: string | number;
  meta_milestone_label?: string;
  meta_heading?: string;
  meta_description?: string;
  meta_priority?: number;
  meta_status?: number | boolean;
}

export interface LifeMilestone1Data {
  id?: string;
  status?: boolean;
  title_prefix?: string;
  title_highlight?: string;
  title_suffix?: string;
  title_highlight_color?: string;
  background_color?: string;
  image_frame_color?: string;
  hero_image?: string;
  hero_image_alt?: string;
  timeline_color?: string;
  auto_rotate_seconds?: number;
  metadata?: LifeMilestone1MetadataRow[];
}

interface LifeMilestone1Props {
  data: LifeMilestone1Data | { data: LifeMilestone1Data };
}

function resolvePayload(
  data: LifeMilestone1Props["data"],
): LifeMilestone1Data | undefined {
  const anyData = data as { data?: LifeMilestone1Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as LifeMilestone1Data;
  }
  return data as LifeMilestone1Data;
}

function isRowActive(row: LifeMilestone1MetadataRow): boolean {
  if (row.meta_status === false || row.meta_status === 0) return false;
  return true;
}

const LG_MIN_WIDTH = "(min-width: 1024px)";

/** Reference (chandigarh / IIT KGP organism): vertical-only flag glide. */
const FLAG_TRANSITION = "transform 1000ms ease-in-out";

/** Gap between consecutive timeline rows, in px (matches reference gap-[40px]). */
const ROW_GAP_PX = 40;

function subscribeLgMq(onChange: () => void) {
  const mq = window.matchMedia(LG_MIN_WIDTH);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}

function getLgSnapshot() {
  return window.matchMedia(LG_MIN_WIDTH).matches;
}

function getLgServerSnapshot() {
  return false;
}

function useIsLg() {
  return useSyncExternalStore(subscribeLgMq, getLgSnapshot, getLgServerSnapshot);
}

const LifeMilestone1 = ({ data }: LifeMilestone1Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headingRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const [active, setActive] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const [headingTops, setHeadingTops] = useState<number[]>([]);
  const [ready, setReady] = useState(false);
  const isLg = useIsLg();

  const {
    status = true,
    title_prefix = "Life after ",
    title_highlight = "IIT Kharagpur",
    title_suffix = "",
    title_highlight_color = "#FFC600",
    background_color = "#291973",
    image_frame_color = "#E1473F",
    hero_image = "",
    hero_image_alt = "",
    timeline_color = "#807070",
    metadata = [],
  } = sectionData || {};

  const steps = useMemo(() => {
    const rows = Array.isArray(metadata) ? metadata.filter(isRowActive) : [];
    return [...rows].sort(
      (a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0),
    );
  }, [metadata]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "life_milestone_1");
  //   }
  // }, [status]);

  useEffect(() => {
    if (steps.length === 0) return;
    setActive((i) => Math.min(i, steps.length - 1));
  }, [steps.length]);

  /** Active step: content row closest to viewport center. */
  useEffect(() => {
    if (steps.length === 0) return;

    rowRefs.current.length = steps.length;

    let raf = 0;
    const syncActiveToScroll = () => {
      const mid = window.innerHeight * 0.5;
      let bestIdx = 0;
      let bestDist = Number.POSITIVE_INFINITY;

      rowRefs.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const rowCenter = r.top + r.height / 2;
        const d = Math.abs(rowCenter - mid);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      });

      setActive((prev) => (prev === bestIdx ? prev : bestIdx));
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncActiveToScroll);
    };

    syncActiveToScroll();
    window.addEventListener("scroll", onScrollOrResize, { passive: true, capture: true });
    window.addEventListener("resize", onScrollOrResize, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScrollOrResize, { capture: true });
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [steps.length]);

  /**
   * Timeline geometry (reference organism):
   *  - containerHeight drives the flag column + divider bar heights
   *  - rowHeights pad each label so labels align row-by-row with the copy
   *  - headingTops give the flag its vertical target (active heading top)
   */
  const measure = useCallback(() => {
    const container = timelineRef.current;
    if (!container) return;
    setContainerHeight(container.clientHeight);
    const cTop = container.getBoundingClientRect().top;
    setRowHeights(steps.map((_, i) => rowRefs.current[i]?.clientHeight ?? 0));
    setHeadingTops(
      steps.map((_, i) => {
        const el = headingRefs.current[i];
        if (!el) return 0;
        return el.getBoundingClientRect().top - cTop;
      }),
    );
    setReady(true);
  }, [steps]);

  useLayoutEffect(() => {
    if (steps.length === 0) return;
    rowRefs.current.length = steps.length;
    headingRefs.current.length = steps.length;
    measure();

    const container = timelineRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;

    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [steps.length, isLg, measure]);

  useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  if (!status || steps.length === 0) return null;

  const flagTop = headingTops[active] ?? 0;

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "w-full px-6 py-10 min-[640px]:px-8 min-[768px]:px-11 min-[768px]:py-[52px]",
      )}
      style={{
        backgroundColor: background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-11 min-[768px]:gap-[42px]">
        <h2 className="text-center text-[28px] font-semibold leading-[1.26] tracking-[-0.02em] text-white min-[1024px]:text-left min-[1024px]:text-[32px] min-[1024px]:leading-none min-[1024px]:tracking-normal">
          <span>{title_prefix}</span>
          <span style={{ color: title_highlight_color }}>{title_highlight}</span>
          {title_suffix ? <span>{title_suffix}</span> : null}
        </h2>

        <div className="flex flex-col items-center gap-10 min-[1024px]:flex-row min-[1024px]:items-start min-[1024px]:justify-between min-[1024px]:gap-10 min-[1280px]:gap-11">
          {/* Timeline: LABELS | FLAG | BAR | COPY (reference organism order) */}
          <div
            ref={timelineRef}
            className="flex w-[330px] shrink-0 gap-3 min-[1024px]:w-[554px] min-[1024px]:gap-10"
          >
            <div className="flex gap-1">
              {/* Labels — each padded to its copy row height so rows align */}
              <div className="flex flex-col" style={{ gap: ROW_GAP_PX }}>
                {steps.map((step, index) => {
                  const isOn = index === active;
                  return (
                    <div
                      key={`label-${step.meta_milestone_label}-${index}`}
                      className="flex flex-col"
                      style={
                        rowHeights[index] != null
                          ? { height: `${rowHeights[index]}px` }
                          : undefined
                      }
                    >
                      <span
                        className={cn(
                          "text-right text-[14px] font-semibold uppercase leading-normal tracking-[-0.03em] text-white transition-opacity duration-300 min-[1024px]:text-left min-[1024px]:text-[18px] min-[1024px]:leading-snug",
                          isOn ? "opacity-100" : "opacity-30 min-[1024px]:opacity-40",
                        )}
                      >
                        {step.meta_milestone_label || `Step ${index + 1}`}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Flag column — single floating flag, vertical-only glide */}
              <div
                className="relative w-5 shrink-0 overflow-hidden min-[1024px]:w-8"
                style={{ height: `${containerHeight}px` }}
                aria-hidden
              >
                <div
                  className="absolute left-0 top-0"
                  style={{
                    transform: `translateY(${flagTop}px)`,
                    opacity: ready ? 1 : 0,
                    transition: `${FLAG_TRANSITION}, opacity 0.2s ease-out`,
                  }}
                >
                  <MilestoneFlagIcon size={isLg ? "default" : "mobile"} />
                </div>
              </div>
            </div>

            {/* Divider bar */}
            <div
              className="w-[2px] shrink-0 min-[1024px]:w-[3px]"
              style={{ backgroundColor: timeline_color, height: `${containerHeight}px` }}
              aria-hidden
            />

            {/* Copy column */}
            <div className="flex min-w-0 flex-1 flex-col" style={{ gap: ROW_GAP_PX }}>
              {steps.map((step, index) => {
                const isOn = index === active;
                return (
                  <div
                    key={`row-${step.meta_milestone_label}-${index}`}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    className={cn(
                      "flex flex-col gap-4 scroll-mt-[min(45vh,200px)] text-left transition-opacity duration-300 min-[1024px]:gap-2",
                      isOn ? "opacity-100" : "opacity-50",
                    )}
                  >
                    <p
                      ref={(el) => {
                        headingRefs.current[index] = el;
                      }}
                      className={cn(
                        "text-[16px] leading-snug text-white transition-[font-weight] duration-300 min-[1024px]:text-[18px] min-[1024px]:leading-normal",
                        isOn ? "font-medium min-[1024px]:font-semibold" : "font-medium",
                      )}
                    >
                      {step.meta_heading}
                    </p>
                    <p className="text-[12px] leading-relaxed tracking-[-0.02em] text-white/95 min-[1024px]:tracking-tight">
                      {step.meta_description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            className={cn(
              "relative flex w-full max-w-[275px] shrink-0 justify-center overflow-hidden rounded-bl-[29.5px] rounded-tr-[29.5px]",
              "aspect-[275/289] min-h-[200px]",
              "min-[1024px]:max-w-[392px] min-[1024px]:rounded-bl-[42px] min-[1024px]:rounded-tr-[42px] min-[1024px]:aspect-[392/411] min-[1024px]:min-h-[220px]",
            )}
            style={{ backgroundColor: image_frame_color }}
          >
            {hero_image ? (
              <Image
                src={hero_image}
                alt={hero_image_alt || "Life milestone hero"}
                fill
                className="object-cover object-center"
                sizes="(max-width: 1024px) 275px, 392px"
              />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LifeMilestone1;
